import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule } from "@angular/router";
import { DxButtonModule, DxTooltipModule } from "devextreme-angular";
import { FileViewerService } from "src/app/services/file-viewer.service";
import { ContractService } from "src/app/services/contracts/contract.service";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";

export interface ProjectStatusEntry {
  key: string;
  label: string;
  icon: string | null;
  glyph?: string;
  borderColor: string;
  iconColor: string;
}

export const ProjectStatusTable: ProjectStatusEntry[] = [
  {
    key: "aktiv",
    label: $localize`:@@projectStatusActive:Active`,
    icon: "dots",
    glyph: undefined,
    borderColor: "var(--color-status-blue)",
    iconColor: "var(--color-status-blue)",
  },
  {
    key: "abgeschlossen",
    label: $localize`:@@projectStatusCompleted:Completed`,
    icon: "check",
    borderColor: "var(--color-status-green)",
    iconColor: "var(--color-status-green)",
  },
  {
    key: "inaktiv",
    label: $localize`:@@projectStatusInactive:Inactive`,
    icon: "pause-bars",
    borderColor: "var(--color-status-orange)",
    iconColor: "var(--color-status-orange)",
  },
  {
    key: "abgebrochen",
    label: $localize`:@@projectStatusCancelled:Cancelled`,
    icon: "close",
    borderColor: "var(--color-status-red)",
    iconColor: "var(--color-status-red)",
  },
];

export function resolveProjectStatusEntry(value: string | null | undefined): ProjectStatusEntry | null {
  if (!value) return null;
  const key = String(value).trim().toLowerCase();
  return ProjectStatusTable.find((entry) => entry.key === key) ?? null;
}

/** Text of a `contractPreviewLink` pill — shared with the contract overview's Projekte header filter (#4865). */
export function previewLinkLabel(link: any): string {
  if (link.recordSource === "project") return `${link.siteNr} - ${link.projectNr} | ${link.projectName}`;
  if (link.recordSource === "contract") return `No Links`;
  return `${link.siteNr} | ${link.siteName}`;
}

@Component({
    selector: "app-contracts-pill",
    imports: [CommonModule, RouterModule, DxButtonModule, DxTooltipModule, SvgIconComponent],
    templateUrl: "./contracts-pill.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./contracts-pill.component.css"]
})
export class ContractsPillComponent implements OnInit, OnChanges {
  @Input() editable: any;

  @Input() contractId!: number;
  @Input() content: any;
  @Input() linkType!: string;

  /** Text shown inside the pill */
  @Input() displayString!: string;

  /** When truthy, shows the document icon; you already used this prop */
  @Input() icon!: string;

  /** Optional quantity badge */
  @Input() quantity: number | null = null;

  /** Optional project status — when provided, renders a status icon prefix and a status-coloured border */
  @Input() projectStatus: string | null | undefined = null;

  /** When true, an internal-link pill (routerLink) opens its target in a new browser tab
   * instead of navigating away from the current page. */
  @Input() openInNewTab: boolean = false;

  statusEntry: ProjectStatusEntry | null = null;

  @Output() onCloseClick = new EventEmitter<void>();
  @Output() onPillClick = new EventEmitter<any>(); // kept for compatibility if a parent listens

  /** For <a [routerLink]> navigation */
  routerCommands: any[] | null = null;

  /** Template flags */
  isInternalLink = false;   // when true → render <a [routerLink]>
  isFileLink = false;       // when true → render <a [href]> or <button> to open via service

  /** If you have a real file URL for doc links, set it; otherwise keep null to use the service */
  fileUrl: string | null = null;

  /** True when the underlying document was edited outside the system in SharePoint */
  get externallyModified(): boolean {
    return !!(this.content && this.content.externallyModified);
  }

  constructor(
    private fileViewerService: FileViewerService,
    private router: Router,
    private contractService: ContractService
  ) {}

  /** Records the current page so a contract's "Abbrechen"/"Cancel" button (and similar
   * back-navigation) returns here instead of falling back to a stale previousUrl - every
   * page that navigates into a contract via routerLink is expected to do this, but this
   * pill is reused so widely that it's safer to do it once, here. */
  onLinkClick(event: MouseEvent): void {
    event.stopPropagation();
    this.contractService.setPreviousUrl(this.router.url);
  }

  ngOnInit(): void {
    this.statusEntry = resolveProjectStatusEntry(this.projectStatus);
    // Compute `displayString`, `routerCommands`, and flags based on linkType/content
    switch (this.linkType) {
      case "contractPreviewLink": {
        this.displayString = previewLinkLabel(this.content);
        if (this.content.recordSource === "project") {
          this.routerCommands = ["/site", this.content.siteId, "project", this.content.projectId];
          this.isInternalLink = true;
        } else if (this.content.recordSource === "contract") {
          this.isInternalLink = false;
        } else {
          // site
          this.routerCommands = ["/site", this.content.siteId];
          this.isInternalLink = true;
        }
        break;
      }

      case "contractLinkName": {
        if (
          this.content.recordSource === "project" ||
          this.content.recordSource === "project task"
        ) {
          this.displayString = `${this.content.siteNr} - ${this.content.projectNr} | ${this.content.projectName}`;
          this.routerCommands = ["/site", this.content.siteId, "project", this.content.projectId];
          this.isInternalLink = true;
        } else if (this.content.recordSource === "project added") {
          this.displayString = this.content.projectName;
          this.routerCommands = ["/site", this.content.siteId, "project", this.content.projectId];
          this.isInternalLink = true;
        } else {
          // site
          this.displayString = `${this.content.siteNr} | ${this.content.siteName}`;
          this.routerCommands = ["/site", this.content.siteId];
          this.isInternalLink = true;
        }
        break;
      }

      case "contractLinkSource": {
        // "Contract" told the reader nothing they didn't already know (they're on the
        // contract). What the column actually distinguishes is whether a link was added
        // directly here vs. pulled in automatically from a task/property owner - say that.
        if (this.content.recordSource === "project") {
          this.displayString = $localize`:@@manuallyAddedContractLink:Manually added`;
          this.isInternalLink = false; // your original code didn’t set a route for this variant
        }
        if (
          this.content.recordSource === "site task" ||
          this.content.recordSource === "project task"
        ) {
          this.displayString = `${this.content.taskNr} | ${this.content.taskTitle}`;
          this.routerCommands = [
            "/site",
            this.content.siteId,
            "project",
            this.content.projectId,
            "tasks",
            this.content.taskId,
          ];
          this.isInternalLink = true;
        }
        if (this.content.recordSource === "property owner") {
          this.displayString = `${this.content.propertyOwnerName}`;
          this.routerCommands = ["/site", this.content.siteId, "properties"];
          this.isInternalLink = true;
        }
        if (this.content.recordSource === "project added") {
          this.displayString = $localize`:@@manuallyAddedContractLink:Manually added`;
          this.isInternalLink = false;
        }
        break;
      }

      case "document": {
        // Opens file via service (or via href if you have a URL)
        this.displayString = `${this.content.documentName}`;
        this.isFileLink = true;
        // If a direct URL exists, set it → file opens natively in new tab:
        // this.fileUrl = this.content.documentUrl ?? null;
        break;
      }

      case "society": {
        this.displayString = `${this.content.legalName}`;
        this.routerCommands = ["/contacts", "society", this.content.societyId];
        this.isInternalLink = true;
        break;
      }

      // How a Gesellschaft holds the contract: display only, like "Manually added".
      case "societyLinkedAs": {
        this.displayString = this.content.isArticlesOfAssociation
          ? $localize`:@@societyArticlesOfAssociation:Articles of association`
          : $localize`:@@contractLinkSocietyFurtherContract:Further contract`;
        this.isInternalLink = false;
        break;
      }

      case "contract": {
        // Safer absolute commands than "./contracts/edit/:id"
        this.displayString = `${this.content.designation}`;
        this.routerCommands = ["/contracts", "edit", this.content.contractId];
        this.isInternalLink = true;
        break;
      }

      // Technical Component link types
      case "techSite": {
        this.displayString = this.content.siteName || this.displayString;
        this.routerCommands = ["/site", this.content.siteId, "infrastructure"];
        this.isInternalLink = true;
        break;
      }

      case "techProject": {
        this.displayString = this.content.projectName || this.displayString;
        this.routerCommands = [
          "/site",
          this.content.siteId,
          "project",
          this.content.projectId,
          "projectInfo",
          "components",
        ];
        this.isInternalLink = true;
        break;
      }

      case "techDocument": {
        this.displayString = this.content.docName || this.displayString;
        this.isFileLink = true;
        // If a direct URL exists, set fileUrl:
        // this.fileUrl = this.content.docLink ?? null;
        break;
      }

      case "contact": {
        // No dedicated contact detail route to link to (contacts are edited via a popup
        // elsewhere) - falls through to the display-only pill styling.
        this.displayString = this.content?.displayName || this.displayString;
        break;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["projectStatus"]) {
      this.statusEntry = resolveProjectStatusEntry(this.projectStatus);
    }
  }

  /** Used when there is no direct file URL and we must open via the viewer service */
  openFile(e: MouseEvent) {
    e.stopPropagation();

    if (this.linkType === "document") {
      this.fileViewerService.openOrDownloadFile(
        "contract",
        null,
        this.content.documentName,
        ""
      );
    } else if (this.linkType === "techDocument") {
      this.fileViewerService.openOrDownloadFile(
        "component",
        this.content.techDocId,
        this.content.docLink ?? "",
        "",
        this.content.fileName
      );
    }
  }

  onClose(e: any) {
    // Both are needed: stopPropagation keeps this click from reaching the wrapping <a>'s
    // routerLink handler at all, but that handler is also what normally cancels the anchor's
    // native navigation (by calling preventDefault itself) - skip it via stopPropagation and
    // the browser falls back to just following the href natively. preventDefault here cancels
    // that native navigation directly, so removing the pill no longer opens its link too.
    e.event?.stopPropagation?.();
    e.event?.preventDefault?.();
    this.onCloseClick.emit(this.content);
  }
}